<template>
    <Teleport to="body">
        <div
            v-if="isVisible"
            ref="tooltipElement"
            class="custom-vue-tooltip"
            :style="tooltipStyle"
            @mouseenter="onTooltipMouseEnter"
            @mouseleave="onTooltipMouseLeave"
            @mousemove.stop
        >
        <slot>Fallback Tooltip Content</slot>
        </div>
    </Teleport>
</template>

<script>
import { debounce } from "lodash";

const MOUSE_MOVE_DEBOUNCE_DELAY = 10; // ms

export default {
    props: {
        showDelay: { type: Number, default: 1000 },
        offset: {
            type: Array,
            default: () => [0, 0],
            validator: (v) => Array.isArray(v) && v.length === 2 && v.every(Number.isFinite),
        },
    },
    data() {
        return {
            isVisible: false,
            tooltipX: 0,
            tooltipY: 0,

            // State related to the trigger element
            visibleForTriggerElement: null, // The trigger responsible for the currently visible tooltip
            pendingTriggerElement: null,    // The trigger that initiated the current pending show timeout

            // Pending Show Timeout
            pendingShowTimeoutId: null,

            // Interaction State
            isMouseOverTooltip: false,     // Flag for hover over the tooltip div itself
            isTrackingMousePosition: false,// Flag for the temporary mousemove listener for positioning
            latestClientX: 0,
            latestClientY: 0,

            // New: State for externally controlled mouse tracking
            isExternalTrackingActive: false,
            latestExternalMouseEvent: null, // Stores the most recent mouse event when external tracking is active

            // Debounced Handler Instance
            debouncedHideCheckHandler: null,
        };
    },
    computed: {
        tooltipStyle() { return { position: 'fixed', left: `${this.tooltipX}px`, top: `${this.tooltipY}px`, zIndex: 10000 }; },
        actualOffsetX() { return this.offset[0]; },
        actualOffsetY() { return this.offset[1]; },
    },
    methods: {
        // --- Mouse position tracking for pending show ---
        _updateLatestMousePosition(event) {
            this.latestClientX = event.clientX;
            this.latestClientY = event.clientY;
        },

        _startMousePositionTracker(initialEvent) {
            // Ensure previous tracker is stopped before starting a new one
            this._stopMousePositionTracker();
            this._updateLatestMousePosition(initialEvent);
            document.addEventListener('mousemove', this._updateLatestMousePosition);
            this.isTrackingMousePosition = true;
        },

        _stopMousePositionTracker() {
            if (this.isTrackingMousePosition) {
                document.removeEventListener('mousemove', this._updateLatestMousePosition);
                this.isTrackingMousePosition = false;
            }
        },

        // --- Public API Methods (Called from Parent Component) ---

        /**
         * Initiates the process to show the tooltip after a delay.
         * Call this on mouseenter of your trigger element.
         * @param {MouseEvent} event - The mouseenter event.
         */
        publicShowIntent(event) {
            if (!event || !event.currentTarget) {
                console.warn('Tooltip: publicShowIntent called without valid event or target.');
                return;
            }
            const triggerElement = event.currentTarget;

            // --- State Reset ---
            // 1. If a tooltip is already visible, hide it immediately. Ensures A->B works cleanly.
            if (this.isVisible) {
                this._hideInternal();
            }
            // 2. Clear any previously pending show timeout (for this tooltip instance).
            this._clearPendingShowTimeout();
            // --- End Reset ---

            // --- Setup new pending show ---
            this.pendingTriggerElement = triggerElement; // Associate trigger with this pending operation
            this._startMousePositionTracker(event);     // Start tracking mouse for accurate position

            this.pendingShowTimeoutId = setTimeout(() => {
                // Check if the trigger associated with *this timeout* is still the one intended to be shown
                // (i.e., hasn't been cancelled or superseded by another publicShowIntent call)
                if (this.pendingTriggerElement === triggerElement) {
                    const positionData = { clientX: this.latestClientX, clientY: this.latestClientY };
                    // Clear pending state *before* showing
                    this._clearPendingShowTimeout(); // Clears timeout, stops tracker, nulls pendingTriggerElement
                    // Proceed to show
                    this._showInternal(triggerElement, positionData);
                }
                // If pendingTriggerElement changed or is null, this timeout is stale, do nothing.
            }, this.showDelay);
        },

        /**
         * Cancels a pending show or signals mouseleave from a trigger.
         * Call this on mouseleave of your trigger element.
         * @param {MouseEvent} event - The mouseleave event.
         */
        publicCancelOrLeave(event) {
            if (!event || !event.currentTarget) {
                // If event is bad, still attempt to clear any generic pending show.
                this._clearPendingShowTimeout();
                return;
            }
            const leavingTrigger = event.currentTarget;

            // If leaving the specific element that has a show pending, cancel it.
            if (this.pendingTriggerElement === leavingTrigger) {
                this._clearPendingShowTimeout();
            }
            // Note: This method does NOT hide an already visible tooltip.
            // Hiding when leaving the trigger (if tooltip is visible) is handled
            // by the document mousemove listener (_checkHideOnDocumentMouseMove).
        },

        /**
         * Immediately shows the tooltip. Bypasses the show delay.
         * Useful for showing after async data fetching.
         * @param {object} positionData - Object with clientX and clientY properties for positioning.
         * @param {HTMLElement} triggerElement - The element to associate the tooltip with.
         */
        publicShowNow(positionData, triggerElement) {
            if (!positionData || !triggerElement) {
                console.warn('Tooltip: publicShowNow called with invalid arguments.');
                return;
            }
            // Ensure clean state
            if (this.isVisible) this._hideInternal();
            this._clearPendingShowTimeout();
            // Show immediately
            this._showInternal(triggerElement, positionData);
        },

        /**
         * Immediately hides the tooltip.
         */
        publicHide() {
            this._hideInternal();
        },

        // --- Internal State Management & Actions ---

        /** Clears any pending show timeout, stops mouse tracking, resets pending trigger. */
        _clearPendingShowTimeout() {
            if (this.pendingShowTimeoutId) {
                clearTimeout(this.pendingShowTimeoutId);
                this.pendingShowTimeoutId = null;
            }
            this._stopMousePositionTracker(); // This is for the internal showDelay tracking
            this.pendingTriggerElement = null;
        },

        /** Shows the tooltip, sets state, adds document listener. */
        _showInternal(triggerElementToShowFor, positionData) {
            // Ensure any pending state is clear (should be, but safety first)
            this._clearPendingShowTimeout();
            // Stop tracker if it was somehow still running
            this._stopMousePositionTracker();

            this.visibleForTriggerElement = triggerElementToShowFor;
            this.tooltipX = positionData.clientX + this.actualOffsetX;
            this.tooltipY = positionData.clientY + this.actualOffsetY;
            this.isVisible = true; // Make visible

            this.$nextTick(() => {
                if (!this.isVisible) return; // Guard against immediate hide race condition
                this.adjustPosition(positionData.clientX, positionData.clientY);
                // Add document listener AFTER tooltip is positioned and visible
                document.addEventListener('mousemove', this.debouncedHideCheckHandler);
            });
        },

        /** Hides the tooltip, resets state, removes document listener. */
        _hideInternal() {
            if (!this.isVisible) return; // Already hidden

            this.isVisible = false; // Set invisible FIRST

            // Cancel pending debounced calls and remove listener for _checkHideOnDocumentMouseMove
            if (this.debouncedHideCheckHandler && this.debouncedHideCheckHandler.cancel) {
                this.debouncedHideCheckHandler.cancel();
            }
            document.removeEventListener('mousemove', this.debouncedHideCheckHandler);

            // Reset all relevant state for internal visibility
            this.visibleForTriggerElement = null;
            this.isMouseOverTooltip = false;
            this._clearPendingShowTimeout(); // Ensure any pending show is also cleared on hide

            // If external tracking was somehow active when hiding, ensure it stops too.
            // This is a safeguard; typically it should be stopped by the parent.
            if (this.isExternalTrackingActive) {
                this.publicStopExternalTracking();
            }
        },

        // --- Document Mousemove Listener ---

        /** Debounced handler called on document mousemove when tooltip is visible. */
        _checkHideOnDocumentMouseMove(event) {
            if (!this.isVisible) return; // Listener should be removed if not visible, but double-check

            // Check 1: Is mouse over the tooltip element itself?
            if (this.isMouseOverTooltip) {
                return; // Safe: Over tooltip
            }

            // Check 2: Is mouse over the trigger element that caused this tooltip?
            if (this.visibleForTriggerElement && this.visibleForTriggerElement.contains(event.target)) {
                return; // Safe: Over the trigger
            }

            // If neither safe zone contains the target, hide.
            this._hideInternal();
        },

        // --- Tooltip Element Events ---
        onTooltipMouseEnter() { this.isMouseOverTooltip = true; },
        onTooltipMouseLeave() { this.isMouseOverTooltip = false; },

        // --- Positioning ---
        adjustPosition(cursorX, cursorY) {
            if (!this.isVisible || !this.$refs.tooltipElement) return;
            const tooltipEl = this.$refs.tooltipElement;
            const tooltipRect = tooltipEl.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            let newX = cursorX + this.actualOffsetX;
            let newY = cursorY + this.actualOffsetY;
            // Flip logic
            if (newX + tooltipRect.width > viewportWidth - 5) newX = cursorX - tooltipRect.width - this.actualOffsetX;
            if (newX < 5) { newX = 5; if (newX + tooltipRect.width > viewportWidth - 5) newX = Math.max(5, viewportWidth - tooltipRect.width - 5); }
            if (newY + tooltipRect.height > viewportHeight - 5) newY = cursorY - tooltipRect.height - this.actualOffsetY;
            if (newY < 5) { newY = 5; if (newY + tooltipRect.height > viewportHeight - 5) newY = Math.max(5, viewportHeight - tooltipRect.height - 5); }
            this.tooltipX = Math.max(5, Math.min(newX, viewportWidth - tooltipRect.width - 5));
            this.tooltipY = Math.max(5, Math.min(newY, viewportHeight - tooltipRect.height - 5));
        },

        // --- New Methods for Externally Controlled Mouse Tracking ---
        _handleExternalMouseMove(event) {
            if (this.isExternalTrackingActive) {
                // Store only clientX and clientY for efficiency if that's all that's needed
                this.latestExternalMouseEvent = { clientX: event.clientX, clientY: event.clientY };
            }
        },

        publicStartExternalTracking(initialEvent) {
            if (this.isExternalTrackingActive) return; // Already tracking
            // console.log("TooltipComponent: Starting external mouse tracking.");
            this.isExternalTrackingActive = true;
            if (initialEvent) {
                this.latestExternalMouseEvent = { clientX: initialEvent.clientX, clientY: initialEvent.clientY };
            }
            document.addEventListener('mousemove', this._handleExternalMouseMove);
        },

        publicStopExternalTracking() {
            if (!this.isExternalTrackingActive) return; // Not tracking
            // console.log("TooltipComponent: Stopping external mouse tracking.");
            document.removeEventListener('mousemove', this._handleExternalMouseMove);
            this.isExternalTrackingActive = false;
            // Optionally clear latestExternalMouseEvent, or leave it for one last read
            // this.latestExternalMouseEvent = null;
        },

        publicGetLatestExternalMousePosition() {
            return this.latestExternalMouseEvent;
        },

        publicConsumeLatestExternalMousePosition() {
            const position = this.publicGetLatestExternalMousePosition(); // Reuse existing getter
            this.publicStopExternalTracking(); // Reuse existing stop method
            return position;
        },

    },
    created() {
        this.debouncedHideCheckHandler = debounce(
            this._checkHideOnDocumentMouseMove, MOUSE_MOVE_DEBOUNCE_DELAY, { leading: false, trailing: true }
        );
    },
    beforeUnmount() {
        // Ensure everything is cleaned up
        this._clearPendingShowTimeout(); // Handles timeout, internal tracker, pending trigger element
        this._hideInternal(); // Handles visibility, internal listener, visible trigger element, also calls clearPendingShowTimeout

        // Ensure external tracking is stopped if component is unmounted
        if (this.isExternalTrackingActive) {
            this.publicStopExternalTracking();
        }
    },
};
</script>

<style scoped>
.custom-vue-tooltip {
    background-color: #FFF;
    color: #6E7C7E;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
    max-width: 400px;
    pointer-events: auto;
    z-index: 10000;
}
</style>